<script setup lang="ts">
/**
 * What is taking the disk: `du` of one directory at a time, by the tmux bridge
 * as the claude user (runner/disk.ts), drilled into by clicking. / takes up to
 * a minute, so the server keeps an answer for five and Refresh asks afresh.
 */
import { computed, onMounted, ref } from "vue";
import { api } from "../lib/api.js";
import { DU_TIMEOUT_MS } from "@shared/disk.js";
import { bytesLabel } from "../lib/bytes.js";
import { timeLabel } from "../lib/calendar.js";
import type { DiskUsage } from "@shared/types.js";

const path = ref("/");
const usage = ref<DiskUsage | null>(null);
const loading = ref(false);
const error = ref("");

let asked = 0;
async function load(fresh = false): Promise<void> {
  const ask = ++asked;
  loading.value = true;
  error.value = "";
  try {
    const q = new URLSearchParams({ path: path.value, ...(fresh ? { fresh: "1" } : {}) });
    const disk = (await api.get<{ disk: DiskUsage }>(`/api/tmux/disk?${q}`, { timeoutMs: DU_TIMEOUT_MS + 30_000 })).disk;
    if (ask === asked) usage.value = disk;
  } catch (err) {
    if (ask === asked) error.value = (err as Error).message;
  } finally {
    if (ask === asked) loading.value = false;
  }
}

function go(to: string): void {
  if (to === path.value && usage.value?.path === to) return;
  path.value = to;
  void load();
}

const join = (dir: string, name: string): string => (dir === "/" ? `/${name}` : `${dir}/${name}`);

/** `/`, `/home`, `/home/claude`… for the breadcrumbs. */
const crumbs = computed(() => {
  const parts = path.value.split("/").filter(Boolean);
  return [{ label: "/", to: "/" }, ...parts.map((p, i) => ({ label: p, to: `/${parts.slice(0, i + 1).join("/")}` }))];
});

const shown = computed(() => (usage.value?.path === path.value ? usage.value : null));
const share = (bytes: number): number => (shown.value?.bytes ? Math.min(100, (bytes / shown.value.bytes) * 100) : 0);

onMounted(() => void load());
</script>

<template>
  <div class="stats-detail">
    <div class="stats-detail-head">
      <nav class="stats-crumbs" aria-label="Directory">
        <template v-for="(c, i) in crumbs" :key="c.to">
          <span v-if="i > 1" class="stats-sub">/</span>
          <button type="button" class="stats-crumb" :disabled="i === crumbs.length - 1" @click="go(c.to)">{{ c.label }}</button>
        </template>
      </nav>
      <span v-if="shown" class="stats-sub">{{ bytesLabel(shown.bytes) }}</span>
      <span style="flex: 1"></span>
      <button class="btn ghost" type="button" :disabled="loading" @click="load(true)">{{ loading ? "Measuring…" : "Refresh" }}</button>
    </div>
    <p v-if="error" class="field-problem">{{ error }}</p>
    <p v-else-if="!shown" class="stats-sub">Measuring {{ path }}…<template v-if="path === '/'"> the whole disk can take a minute.</template></p>
    <template v-if="shown">
      <ul class="stats-disk">
        <li v-for="e in shown.entries" :key="e.name">
          <button
            v-if="e.dir"
            type="button"
            class="stats-disk-row dir"
            :title="`Open ${join(shown.path, e.name)}`"
            @click="go(join(shown.path, e.name))"
          >
            <span class="stats-disk-bar" :style="{ width: share(e.bytes) + '%' }"></span>
            <span class="stats-disk-name">{{ e.name }}/</span>
            <span class="stats-disk-size">{{ bytesLabel(e.bytes) }}</span>
          </button>
          <div v-else class="stats-disk-row" :title="join(shown.path, e.name)">
            <span class="stats-disk-bar" :style="{ width: share(e.bytes) + '%' }"></span>
            <span class="stats-disk-name">{{ e.name }}</span>
            <span class="stats-disk-size">{{ bytesLabel(e.bytes) }}</span>
          </div>
        </li>
        <li v-if="shown.otherBytes">
          <div class="stats-disk-row other">
            <span class="stats-disk-name">everything else</span>
            <span class="stats-disk-size">{{ bytesLabel(shown.otherBytes) }}</span>
          </div>
        </li>
      </ul>
      <p class="stats-sub stats-detail-note">
        Measured {{ timeLabel(shown.readAt) }}, as the claude user<template v-if="shown.partial">; some directories it cannot read were left out</template>.
      </p>
    </template>
  </div>
</template>
